import * as React from "react"

import { cn } from "@/lib/utils"

/* FOOTGUN: stem and node are ABSOLUTELY POSITIONED, never grid children.
   Positional CSS like `> *:nth-child(n+3) { display: none }` breaks it
   because timeline nodes occupy no track. */

export interface VersionEntry {
  label: string
  printedOn: string
  uploadedAgo?: string
  reportSentOn?: string
  current?: boolean
  state?: "queued" | "in_review" | "needs_sme" | "cleared"
  delta?: { fixed: number; open: number; regressed: number }
  findingCount?: number
}

export interface VersionTimelineProps {
  versions: VersionEntry[]
  onOpen?: (label: string, current: boolean) => void
  compact?: boolean
  className?: string
}

export const VersionTimeline = React.forwardRef<HTMLOListElement, VersionTimelineProps>(
  function VersionTimeline({ versions, onOpen, compact = false, className }, ref) {
    return (
      <ol ref={ref} className={cn("relative flex flex-col", className)}>
      {versions.map((v, i) => (
        <li
          key={v.label}
          className={cn(
            "relative grid items-center gap-3.5 py-2.5 pl-6",
            compact
              ? "grid-cols-[minmax(0,1fr)_auto]"
              : "grid-cols-[minmax(0,1fr)_200px_auto]",
            i > 0 && "border-t border-dashed border-border",
          )}
        >
          {i < versions.length - 1 && (
            <span
              aria-hidden="true"
              className="absolute bottom-0 left-1.5 top-1/2 w-px bg-border"
            />
          )}
          {i > 0 && (
            <span
              aria-hidden="true"
              className="absolute left-1.5 top-0 h-1/2 w-px bg-border"
            />
          )}
          <span
            aria-hidden="true"
            className={cn(
              "absolute left-0 top-1/2 size-2.5 -translate-y-1/2 rounded-full border-2",
              v.current ? "border-brand bg-brand" : "border-n-400 bg-surface",
            )}
          />

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "text-body-sm font-medium",
                  v.current ? "text-brand" : "text-text",
                )}
              >
                {v.label}
              </span>
              {v.current && (
                <span className="font-id text-micro uppercase tracking-[0.1em] text-text-3">
                  current
                </span>
              )}
            </div>
            <div className="mt-0.5 font-id text-micro text-text-3">
              printed {v.printedOn}
              {v.uploadedAgo && ` · uploaded ${v.uploadedAgo}`}
              {v.reportSentOn && ` · report sent ${v.reportSentOn}`}
            </div>
          </div>

          {!compact && (
            <div className="font-id text-caption">
              {v.delta ? (
                <span className="flex flex-wrap items-center gap-x-1.5">
                  <span className="text-master">{v.delta.fixed} fixed</span>
                  <span className="text-border">·</span>
                  <span className="text-err">{v.delta.open} still open</span>
                  {v.delta.regressed > 0 && (
                    <>
                      <span className="text-border">·</span>
                      <span className="text-err">
                        {v.delta.regressed} came back
                      </span>
                    </>
                  )}
                </span>
              ) : (
                <span className="text-text-3">
                  {v.findingCount} findings reported
                </span>
              )}
            </div>
          )}

          {onOpen && (
            <button
              type="button"
              onClick={() => {
                onOpen(v.label, Boolean(v.current))
              }}
              className={cn(
                "rounded-md px-2.5 py-1 text-caption font-medium",
                "transition-colors duration-120 ease-out",
                v.current
                  ? "border border-border bg-surface text-text-2 hover:border-border-strong hover:text-text"
                  : "text-text-3 hover:bg-surface-sunken hover:text-text",
              )}
            >
              {v.current ? "Open" : "Report"}
            </button>
          )}
        </li>
        ))}
      </ol>
    )
  },
)
